import { fromMarkdown } from 'mdast-util-from-markdown';
import { parseFragment } from 'parse5';

/**
 * Text edits behind the description toolbar. Each takes the textarea text and
 * selection and returns the new text plus the selection to restore, so the
 * host can keep typing over a placeholder. Descriptions are stored as plain
 * Markdown/HTML text; these only insert syntax, they never change the format.
 *
 * Markdown is not parsed inside an HTML block (CommonMark), so where the
 * caret sits in pasted HTML the edits insert the HTML equivalent instead.
 */
export interface TextEdit {
  text: string;
  selStart: number;
  selEnd: number;
}

// Minimal view of the mdast tree; only types and offsets are read.
interface MdNode {
  type: string;
  position?: { start: { offset?: number }; end: { offset?: number } };
  children?: MdNode[];
}

// Block containers whose children can be HTML blocks. Inline HTML sits inside
// a paragraph or heading, where Markdown still works, so the walk stops there.
const CONTAINERS = new Set(['root', 'blockquote', 'list', 'listItem', 'footnoteDefinition']);

interface HtmlBlock {
  start: number;
  end: number;
  /** Inside a Markdown quote, so its lines carry "> " markers. */
  quoted: boolean;
}

/** The HTML block around `pos` (same parser as the renderer), or null. */
function htmlBlockAt(text: string, pos: number): HtmlBlock | null {
  const walk = (node: MdNode, quoted: boolean): HtmlBlock | null => {
    for (const child of node.children ?? []) {
      const from = child.position?.start.offset ?? -1;
      const to = child.position?.end.offset ?? -1;
      if (pos < from || pos > to) continue;
      if (child.type === 'html') return { start: from, end: to, quoted };
      if (CONTAINERS.has(child.type)) {
        const found = walk(child, quoted || child.type === 'blockquote');
        if (found) return found;
      }
    }
    return null;
  };
  return walk(fromMarkdown(text) as MdNode, false);
}

/**
 * Whether `pos` sits inside an HTML block, where Markdown would show as raw
 * text. Uses the same parser as the renderer (react-markdown), so it follows
 * CommonMark exactly, including blocks inside quotes and lists.
 */
export function inHtmlBlock(text: string, pos: number): boolean {
  return htmlBlockAt(text, pos) !== null;
}

/** The HTML block a selection lies in, null for Markdown, 'mixed' across both. */
function selectionBlock(text: string, start: number, end: number): HtmlBlock | null | 'mixed' {
  const a = htmlBlockAt(text, start);
  const b = start === end ? a : htmlBlockAt(text, end);
  if (!a && !b) return null;
  if (!a || !b || a.start !== b.start) return 'mixed';
  return a;
}

// Minimal view of the parse5 tree (the HTML parser rehype-raw uses).
interface Loc {
  startOffset: number;
  endOffset: number;
}
interface HNode {
  nodeName: string;
  tagName?: string;
  attrs?: Array<{ name: string; value: string }>;
  childNodes?: HNode[];
  sourceCodeLocation?: (Loc & { startTag?: Loc; endTag?: Loc }) | null;
}

interface Spot {
  /** In a text node, between nodes, or inside a tag or comment. */
  kind: 'text' | 'gap' | 'markup';
  textNode: HNode | null;
  /** Elements around the spot, outermost first. */
  chain: HNode[];
}

const VOID_TAGS = new Set(
  'area base br col embed hr img input link meta source track wbr'.split(' ')
);

/** Where `offset` falls in the parsed HTML of a block. */
function spotAt(root: HNode, offset: number): Spot {
  const chain: HNode[] = [];
  let nodes = root.childNodes ?? [];
  for (;;) {
    let next: HNode | null = null;
    for (const n of nodes) {
      const loc = n.sourceCodeLocation;
      if (!loc || offset < loc.startOffset || offset > loc.endOffset) continue;
      if (n.nodeName === '#text') return { kind: 'text', textNode: n, chain };
      const inside = (l?: Loc) => !!l && offset > l.startOffset && offset < l.endOffset;
      if (!n.tagName) {
        if (inside(loc)) return { kind: 'markup', textNode: null, chain };
        continue;
      }
      if (inside(loc.startTag) || inside(loc.endTag))
        return { kind: 'markup', textNode: null, chain };
      const from = loc.startTag?.endOffset ?? loc.startOffset;
      const to = loc.endTag?.startOffset ?? loc.endOffset;
      if (offset >= from && offset <= to && !VOID_TAGS.has(n.tagName)) {
        next = n;
        break;
      }
    }
    if (!next) return { kind: 'gap', textNode: null, chain };
    chain.push(next);
    nodes = next.childNodes ?? [];
  }
}

const parseBlock = (text: string, block: HtmlBlock) =>
  parseFragment(text.slice(block.start, block.end), {
    sourceCodeLocationInfo: true,
  }) as unknown as HNode;

// Elements whose direct content cannot be inline text or tags.
const NO_PHRASING = new Set(
  (
    'audio colgroup datalist dl head html menu ol optgroup picture select table tbody tfoot ' +
    'thead tr ul video'
  ).split(' ')
);
// Elements whose content is raw text: tags typed there would show as text.
const RAW_TEXT = new Set(
  'iframe noembed noframes noscript plaintext script style textarea title xmp'.split(' ')
);

/**
 * Where an inline edit (bold, link) on [start, end) may go inside an HTML
 * block: one text node, or a gap, in an element that takes inline content.
 * Null anywhere else (inside a tag or comment, across tags, between list
 * items, in raw text), where the edit is skipped.
 */
function inlineSpot(text: string, start: number, end: number, block: HtmlBlock): Spot | null {
  const root = parseBlock(text, block);
  const a = spotAt(root, start - block.start);
  const b = start === end ? a : spotAt(root, end - block.start);
  if (a.kind === 'markup' || b.kind === 'markup') return null;
  if (start !== end && (a.kind !== 'text' || a.textNode !== b.textNode)) return null;
  const tag = a.chain[a.chain.length - 1]?.tagName;
  if (tag && (NO_PHRASING.has(tag) || RAW_TEXT.has(tag))) return null;
  return a;
}

const unchangedEdit = (text: string, start: number, end: number): TextEdit => ({
  text,
  selStart: start,
  selEnd: end,
});

/** Wrap the selection in bold, unwrap it if already bold, or insert a placeholder. */
export function applyBold(text: string, start: number, end: number): TextEdit {
  const block = selectionBlock(text, start, end);
  if (block === 'mixed' || (block && !inlineSpot(text, start, end, block))) {
    return unchangedEdit(text, start, end);
  }
  const [open, close] = block ? ['<strong>', '</strong>'] : ['**', '**'];
  if (
    text.slice(start - open.length, start) === open &&
    text.slice(end, end + close.length) === close
  ) {
    return {
      text:
        text.slice(0, start - open.length) +
        text.slice(start, end) +
        text.slice(end + close.length),
      selStart: start - open.length,
      selEnd: end - open.length,
    };
  }
  const inner = text.slice(start, end) || 'bold text';
  return {
    text: `${text.slice(0, start)}${open}${inner}${close}${text.slice(end)}`,
    selStart: start + open.length,
    selEnd: start + open.length + inner.length,
  };
}

const LIST_ITEM = /^(\s*)[-*] /;

/** Turn the lines touched by the selection into a bullet list, or back if they all are. */
export function applyList(text: string, start: number, end: number): TextEdit {
  // Lines on both sides of a Markdown/HTML boundary cannot become one list.
  const htmlBlock = selectionBlock(text, start, end);
  if (htmlBlock === 'mixed') return unchangedEdit(text, start, end);
  const lineStart = text.lastIndexOf('\n', start - 1) + 1;
  // A selection ending right after a newline does not include the next line.
  const lastChar = end > start && text[end - 1] === '\n' ? end - 1 : end;
  const found = text.indexOf('\n', lastChar);
  const lineEnd = found === -1 ? text.length : found;
  const lines = text.slice(lineStart, lineEnd).split('\n');
  if (htmlBlock) return htmlList(text, start, end, lineStart, lineEnd, htmlBlock);
  const filled = lines.filter((l) => l.trim() !== '');
  const allListed = filled.length > 0 && filled.every((l) => LIST_ITEM.test(l));
  const block = lines
    .map((l) => {
      if (allListed) return l.replace(LIST_ITEM, '$1');
      if (l.trim() === '' && lines.length > 1) return l;
      return LIST_ITEM.test(l) ? l : `- ${l}`;
    })
    .join('\n');
  const caret = lineStart + block.length;
  return {
    text: text.slice(0, lineStart) + block + text.slice(lineEnd),
    selStart: start === end ? caret : lineStart,
    selEnd: caret,
  };
}

// Container markers a line keeps when it changes: indentation, plus "> "
// quote markers when the block sits in a Markdown quote (elsewhere a leading
// ">" is real text).
const listPatterns = (quoted: boolean) => {
  const pre = quoted ? '[ \\t]*(?:>[ \\t]?)*' : '[ \\t]*';
  return {
    prefix: new RegExp(`^${pre}`),
    // One item per line; the content itself holds no other <li> tags.
    item: new RegExp(`^(${pre})<li>((?:(?!<\\/?li[\\s>]).)*)<\\/li>[ \\t]*$`),
    open: new RegExp(`^${pre}<ul>[ \\t]*$`),
    close: new RegExp(`^${pre}<\\/ul>[ \\t]*$`),
  };
};

// Elements a <ul> may sit in. Inside anything else (p, span, pre, a heading)
// the List button does nothing rather than write invalid HTML.
const FLOW_PARENTS = new Set(
  (
    'article aside blockquote body center dd details dialog div fieldset figure footer form ' +
    'header html li main nav search section td th'
  ).split(' ')
);

/**
 * applyList inside an HTML block. It does exactly two things: wrap plain
 * lines (no list tags) in <ul><li>, or turn items of the list it creates
 * itself (a bare <ul>, one <li> per line) back into plain lines, splitting the
 * list around them. Any other list markup is left untouched, so pasted HTML is
 * never damaged. Quote markers and indentation stay on every line.
 */
function htmlList(
  text: string,
  start: number,
  end: number,
  lineStart: number,
  lineEnd: number,
  htmlBlock: HtmlBlock
): TextEdit {
  const P = listPatterns(htmlBlock.quoted);
  const prefixOf = (line: string) => P.prefix.exec(line)?.[0] ?? '';
  const bodyOf = (line: string) => line.slice(prefixOf(line).length).trim();
  const HTML_ITEM = P.item;
  const UL_OPEN = P.open;
  const UL_CLOSE = P.close;
  const root = parseBlock(text, htmlBlock);
  const spot = (offset: number) => spotAt(root, offset - htmlBlock.start);
  const all = text.split('\n');
  const rowOf = (offset: number) => text.slice(0, offset).split('\n').length - 1;
  const first = rowOf(lineStart);
  const last = rowOf(lineEnd);
  const rows = [...Array(last - first + 1).keys()].map((i) => first + i);
  const content = rows.filter((r) => {
    const l = all[r];
    return bodyOf(l) !== '' && !UL_OPEN.test(l) && !UL_CLOSE.test(l);
  });
  const lengthUpTo = (lines: string[], count: number) =>
    lines.slice(0, count).reduce((n, l) => n + l.length + 1, 0);

  const unchanged = { text, selStart: start, selEnd: end };

  if (content.length > 0 && content.every((r) => HTML_ITEM.test(all[r]))) {
    // The enclosing <ul> ... </ul>, reached through item and blank lines only.
    const passable = (l: string) => HTML_ITEM.test(l) || bodyOf(l) === '';
    let open = first;
    while (open >= 0 && !UL_OPEN.test(all[open]) && passable(all[open])) open -= 1;
    let close = last;
    while (close < all.length && !UL_CLOSE.test(all[close]) && passable(all[close])) close += 1;
    const wrapped =
      open >= 0 && UL_OPEN.test(all[open]) && close < all.length && UL_CLOSE.test(all[close]);
    // Items inside <ol>, <ul class="...">, etc. are not ours to unwrap.
    if (!wrapped) return unchanged;
    // Confirm with the parser over <ul> ... </ul> (blank lines may split it into
    // several Markdown blocks): every touched item is an <li> straight inside a
    // bare <ul>, and the <ul> itself is not inside a comment.
    const listStart = lengthUpTo(all, open) + prefixOf(all[open]).length;
    const listRoot = parseFragment(
      text.slice(listStart, lengthUpTo(all, close) + all[close].length),
      {
        sourceCodeLocationInfo: true,
      }
    ) as unknown as HNode;
    const ownItems = content.every((r) => {
      const inItem = lengthUpTo(all, r) + prefixOf(all[r]).length + '<li>'.length;
      const at = spotAt(listRoot, inItem - listStart);
      const [ul, li] = at.chain;
      return (
        at.kind !== 'markup' &&
        at.chain.length === 2 &&
        ul.tagName === 'ul' &&
        !ul.attrs?.length &&
        li.tagName === 'li'
      );
    });
    const openBlock = htmlBlockAt(text, listStart);
    const ulSpot = openBlock && spotAt(parseBlock(text, openBlock), listStart - openBlock.start);
    if (!ownItems || ulSpot?.kind === 'markup') return unchanged;
    const from = open;
    const to = close;
    const itemRows = [...Array(to - from + 1).keys()]
      .map((i) => from + i)
      .filter((r) => HTML_ITEM.test(all[r]));
    const before = itemRows.filter((r) => r < content[0]).map((r) => all[r]);
    const after = itemRows.filter((r) => r > content[content.length - 1]).map((r) => all[r]);
    const plain = content.map((r) => all[r].replace(HTML_ITEM, '$1$2'));
    const openLine = all[open];
    const closeLine = all[close];
    const head = before.length ? [openLine, ...before, closeLine] : [];
    const out = [...head, ...plain, ...(after.length ? [openLine, ...after, closeLine] : [])];
    const next = [...all.slice(0, from), ...out, ...all.slice(to + 1)];
    const selStart = lengthUpTo(all, from) + lengthUpTo(head, head.length);
    return { text: next.join('\n'), selStart, selEnd: selStart + plain.join('\n').length };
  }

  // Wrap only whole lines of plain text (one text node, no tags) whose
  // element can hold a list; anything else is left as it is.
  const a = spot(lineStart);
  const b = spot(lineEnd);
  if (a.kind !== 'text' || b.kind !== 'text' || a.textNode !== b.textNode) return unchanged;
  const parent = a.chain[a.chain.length - 1]?.tagName;
  if (parent && !FLOW_PARENTS.has(parent)) return unchanged;

  if (content.length === 0) {
    const p = prefixOf(all[first]);
    const block = [`${p}<ul>`, `${p}<li></li>`, `${p}</ul>`];
    const caret = lineStart + block[0].length + 1 + `${p}<li>`.length;
    return {
      text: `${text.slice(0, lineStart)}${block.join('\n')}${text.slice(lineEnd)}`,
      selStart: caret,
      selEnd: caret,
    };
  }

  const items = content.map((r) => `${prefixOf(all[r])}<li>${bodyOf(all[r])}</li>`);
  const block = [
    `${prefixOf(all[content[0]])}<ul>`,
    ...items,
    `${prefixOf(all[content[content.length - 1]])}</ul>`,
  ];
  // Select the items (not the <ul> lines) so clicking List again unwraps them.
  const itemsStart = lineStart + block[0].length + 1;
  const itemsEnd = itemsStart + items.join('\n').length;
  // A caret stays inside the last item (before its </li>) so typing extends it.
  const caret = itemsEnd - '</li>'.length;
  return {
    text: text.slice(0, lineStart) + block.join('\n') + text.slice(lineEnd),
    selStart: start === end ? caret : itemsStart,
    selEnd: start === end ? caret : itemsEnd,
  };
}

const BARE_URL = /^https?:\/\/\S+$/;

/**
 * Insert [label](url). With text selected the URL part is selected for pasting;
 * a selected URL becomes the link target; with nothing selected the label
 * placeholder is selected for typing.
 */
export function applyLink(text: string, start: number, end: number): TextEdit {
  const block = selectionBlock(text, start, end);
  if (block === 'mixed') return unchangedEdit(text, start, end);
  if (block) {
    const at = inlineSpot(text, start, end, block);
    // Links cannot nest: inside an existing <a>, do nothing.
    if (!at || at.chain.some((n) => n.tagName === 'a')) return unchangedEdit(text, start, end);
    return htmlLink(text, start, end);
  }
  const sel = text.slice(start, end);
  const before = text.slice(0, start);
  const after = text.slice(end);
  if (BARE_URL.test(sel)) {
    const label = 'link text';
    return {
      text: `${before}[${label}](${sel})${after}`,
      selStart: start + 1,
      selEnd: start + 1 + label.length,
    };
  }
  const url = 'https://';
  if (sel) {
    const urlStart = start + sel.length + 3;
    return {
      text: `${before}[${sel}](${url})${after}`,
      selStart: urlStart,
      selEnd: urlStart + url.length,
    };
  }
  const label = 'link text';
  return {
    text: `${before}[${label}](${url})${after}`,
    selStart: start + 1,
    selEnd: start + 1 + label.length,
  };
}

/** applyLink inside an HTML block: the same selections, as an <a> tag. */
function htmlLink(text: string, start: number, end: number): TextEdit {
  const sel = text.slice(start, end);
  const before = text.slice(0, start);
  const after = text.slice(end);
  const tag = (href: string, label: string) => `<a href="${href}">${label}</a>`;
  if (BARE_URL.test(sel)) {
    const label = 'link text';
    const labelStart = start + `<a href="${sel}">`.length;
    return {
      text: before + tag(sel, label) + after,
      selStart: labelStart,
      selEnd: labelStart + label.length,
    };
  }
  const url = 'https://';
  const urlStart = start + '<a href="'.length;
  if (sel) {
    return {
      text: before + tag(url, sel) + after,
      selStart: urlStart,
      selEnd: urlStart + url.length,
    };
  }
  const label = 'link text';
  const labelStart = start + `<a href="${url}">`.length;
  return {
    text: before + tag(url, label) + after,
    selStart: labelStart,
    selEnd: labelStart + label.length,
  };
}

/** Replace the selection with `snippet` and put the caret after it. */
export function insertSnippet(text: string, start: number, end: number, snippet: string): TextEdit {
  const caret = start + snippet.length;
  return { text: text.slice(0, start) + snippet + text.slice(end), selStart: caret, selEnd: caret };
}
